// src/pages/CustomChartsPage.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Breadcrumbs,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SaveIcon from '@mui/icons-material/Save';
import DescriptionIcon from '@mui/icons-material/Description';
import ViewListIcon from '@mui/icons-material/ViewList';
import CategoryIcon from '@mui/icons-material/Category';
import { request } from "../utils/request";
import ChartCard from "./Dashboard/ChartCard";
import ChartRenderer from "./Dashboard/ChartRenderer";
import { enterpriseChartTheme } from "./Dashboard/chartTheme";
import { ColorModeContext } from "../index";

const chartTypes = [
  { value: "bar", label: "Bar Chart" },
  { value: "line", label: "Line Chart" },
  { value: "pie", label: "Pie Chart" },
];

const aggregationTypes = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
];

const emptyForm = {
  title: "",
  description: "",
  chartType: "bar",
  modelName: "",
  xField: "createdAt",
  yField: "_id",
  xLabel: "",
  yLabel: "",
  aggregationType: "count",
  filters: [],
  layout: { x: 0, y: 0, w: 6, h: 4, order: 1 },
  appearance: { primaryColor: "#3B82F6", secondaryColor: "#10B981" },
  cacheEnabled: false,
  cacheDuration: 300,
  isActive: true,
};

const CustomChartsPage = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];

  const checkPerm = (action) => {
    if (isSuperAdmin) return true;
    return userPermissions.some((permission) => {
      const normalizedPermission = String(permission).toLowerCase();
      return (
        (normalizedPermission.includes('custom charts') ||
          normalizedPermission.includes('insight builder')) &&
        normalizedPermission.includes(action.toLowerCase())
      );
    });
  };

  const canCreate = checkPerm('write');
  const canEdit = checkPerm('update');
  const canDelete = checkPerm('delete');

  const [charts, setCharts] = useState([]);
  const [models, setModels] = useState([]);
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");

  const showSnack = (msg, severity = 'success') => {
    setSnackMsg(msg);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const loadCharts = async () => {
    try {
      const res = await request("customcharts", { method: "GET" });
      setCharts(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadModels = async () => {
    try {
      const res = await request("customcharts/models", { method: "GET" });
      setModels(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
  };

  const loadFields = async (modelName) => {
    if (!modelName) {
      setFields([]);
      return;
    }
    try {
      const res = await request(`customcharts/models/${encodeURIComponent(modelName)}/fields`, { method: "GET" });
      setFields(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setFields([]);
    }
  };

  useEffect(() => {
    loadCharts();
    loadModels();
  }, []);

  useEffect(() => {
    loadFields(form.modelName);
  }, [form.modelName]);

  const handleOpenCreate = () => {
    if (!canCreate) return;
    setForm(emptyForm);
    setPreviewData([]);
    setEditingId(null);
    setOpen(true);
  };

  const handleOpenEdit = (chart) => {
    if (!canEdit) return;
    setEditingId(chart._id);
    setForm({
      ...emptyForm,
      ...chart,
      modelName: chart.modelName || "",
      xField: chart.xField || "createdAt",
      yField: chart.yField || "_id",
      layout: chart.layout || emptyForm.layout,
      appearance: chart.appearance || emptyForm.appearance,
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setPreviewData([]);
    setEditingId(null);
  };

  const previewChart = async () => {
    if (!form.modelName) return;
    setLoading(true);
    try {
      const res = await request("customcharts/preview", {
        method: "POST",
        body: {
          ...form,
          filters: Array.isArray(form.filters) ? form.filters : [],
        },
      });
      setPreviewData(Array.isArray(res?.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setPreviewData([]);
    } finally {
      setLoading(false);
    }
  };

  const saveChart = async () => {
    if (editingId && !canEdit) return;
    if (!editingId && !canCreate) return;

    if (!form.title.trim()) {
      showSnack("Title is required.", "error");
      return;
    }
    if (!form.chartType) {
      showSnack("Chart Type is required.", "error");
      return;
    }
    if (!form.modelName) {
      showSnack("Model is required.", "error");
      return;
    }
    if (!form.aggregationType) {
      showSnack("Aggregation is required.", "error");
      return;
    }
    if (!form.xField) {
      showSnack("X Axis Field is required.", "error");
      return;
    }
    if (!form.yField) {
      showSnack("Y Axis Field is required.", "error");
      return;
    }
    if (!form.xLabel.trim()) {
      showSnack("X Label is required.", "error");
      return;
    }
    if (!form.yLabel.trim()) {
      showSnack("Y Label is required.", "error");
      return;
    }

    try {
      const payload = {
        ...form,
        filters: Array.isArray(form.filters) ? form.filters : [],
      };
      if (editingId) {
        await request(`customcharts/${editingId}`, { method: "PUT", body: payload });
      } else {
        await request("customcharts", { method: "POST", body: payload });
      }
      handleClose();
      loadCharts();
    } catch (error) {
      console.error(error);
    }
  };

  const deleteChart = async (id) => {
    if (!canDelete) return;
    try {
      await request(`customcharts/${id}`, { method: "DELETE" });
      loadCharts();
    } catch (error) {
      console.error(error);
    }
  };

  const renderChart = useMemo(() => {
    if (!previewData.length) {
      return (
        <Box
          className="bg-mist text-secondary"
          sx={{
            minHeight: 260,
            border: "1px dashed var(--hairline)",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 2,
            textAlign: "center",
          }}
        >
          Preview will appear here after you run the preview.
        </Box>
      );
    }

    return (
      <ChartRenderer 
        data={previewData} 
        chartType={form.chartType || "bar"} 
        xKey="name" 
        yKey="value" 
        colors={[
          form.appearance?.primaryColor || enterpriseChartTheme.colors[0], 
          form.appearance?.secondaryColor || enterpriseChartTheme.colors[1]
        ]} 
      />
    );
  }, [form.chartType, form.appearance, previewData]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Fixed Header with Breadcrumbs */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.95rem' }}>
          <Link to="/home" className="global-link" style={{ textDecoration: "none" }}>
            Dashboard
          </Link>
          <Typography className="global-section-title" sx={{ fontSize: '0.95rem', margin: 0 }}>
            Insight Builder
          </Typography>
        </Breadcrumbs>
        
        {canCreate && (
          <Button 
            className="global-button global-button-primary" 
            onClick={handleOpenCreate} 
            sx={{ px: 3, borderRadius: 999 }}
          >
            Create Chart
          </Button>
        )}
      </Stack>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} className="global-card" sx={{ p: 3 }}>
            <Typography variant="h6" className="global-section-title" mb={2}>
              Saved Charts
            </Typography>
            <Stack spacing={2}>
              {charts.map((chart) => (
                <ChartCard
                  key={chart._id}
                  title={chart.title || "Untitled chart"}
                  description={chart.description || "No description provided"}
                  meta={{ 
                    modelName: chart.modelName || "Collection", 
                    lastUpdated: chart.updatedAt ? new Date(chart.updatedAt).toLocaleDateString() : "Recently updated", 
                    subtitle: `${chart.chartType || "bar"} • ${chart.aggregationType || "count"}` 
                  }}
                  isActive={chart.isActive !== false}
                  onEdit={canEdit ? () => handleOpenEdit(chart) : undefined}
                  onDelete={canDelete ? () => deleteChart(chart._id) : undefined}
                  onRefresh={() => previewChart(chart)}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip label={chart.modelName} size="small" className="global-chip bg-blue-tint text-interactive" sx={{ m: 0 }} />
                      <Chip label={chart.chartType} size="small" className="global-chip bg-mist text-secondary" sx={{ m: 0 }} />
                      <Chip label={chart.aggregationType} size="small" className="global-chip signal-secure-bg text-white" sx={{ m: 0 }} />
                    </Stack>
                    <Box sx={{ height: 220, mt: 0.5 }}>
                      <ChartRenderer 
                        data={chart.previewData || []} 
                        chartType={chart.chartType || "bar"} 
                        xKey="name" 
                        yKey="value" 
                        colors={[
                          chart.appearance?.primaryColor || enterpriseChartTheme.colors[0], 
                          chart.appearance?.secondaryColor || enterpriseChartTheme.colors[1]
                        ]} 
                      />
                    </Box>
                  </Stack>
                </ChartCard>
              ))}
              {!charts.length && (
                <Box 
                  className="bg-mist text-secondary"
                  sx={{ 
                    border: "1px dashed var(--hairline)", 
                    borderRadius: 3, 
                    py: 5, 
                    textAlign: "center" 
                  }}
                >
                  <Typography>No charts saved yet. Create your first chart to start building your analytics workspace.</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} className="global-card" sx={{ p: 3 }}>
            <Typography variant="h6" className="global-section-title" mb={1}>
              Chart Builder
            </Typography>
            <Typography variant="body2" className="text-secondary">
              Preview your configuration before saving it.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(11, 37, 69, 0.5)',
            backdropFilter: 'blur(5px)'
          }
        }}
        PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden', height: { xs: 'calc(100% - 32px)', md: '85vh' }, maxHeight: { xs: 'none', md: 750 }, m: { xs: 2, md: 4 } } }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ height: '100%' }}>
          {/* Left Sidebar */}
          <Box sx={{ 
            width: { xs: '100%', md: 300 }, 
            display: { xs: 'none', md: 'block' },
            flexShrink: 0, 
            bgcolor: isDark ? 'linear-gradient(180deg, #0b1120 0%, #151C2B 100%)' : '#f8fafc', 
            p: 3, 
            borderRight: '1px solid var(--hairline)',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
          }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                width: 48, height: 48, borderRadius: 3, bgcolor: isDark ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff', 
                color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 
              }}>
                <BarChartIcon />
              </Box>
              <Typography variant="h6" fontWeight="bold" mb={1} color="text.primary">
                {editingId ? "Edit Chart" : "Create Chart"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure your chart settings and preview the visualization.
              </Typography>
            </Box>

            <Stack spacing={1.5}>
              {/* Step 1: Chart Details */}
              <Box sx={{ 
                px: 2, py: 1.5, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'flex-start'
              }}>
                <Box sx={{ 
                  width: 36, height: 36, borderRadius: '50%', bgcolor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe', 
                  color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                }}>
                  <BarChartIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">Chart Details</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.5 }}>Set the basic chart information.</Typography>
                </Box>
              </Box>

              {/* Step 2: Data Source */}
              <Box sx={{ 
                px: 2, py: 1.5, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'flex-start'
              }}>
                <Box sx={{ 
                  width: 36, height: 36, borderRadius: '50%', bgcolor: isDark ? 'rgba(168, 85, 247, 0.15)' : '#f3e8ff', 
                  color: '#a855f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                }}>
                  <StorageIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">Data Source</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.5 }}>Choose fields and aggregation.</Typography>
                </Box>
              </Box>

              {/* Step 3: Configuration */}
              <Box sx={{ 
                px: 2, py: 1.5, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'flex-start'
              }}>
                <Box sx={{ 
                  width: 36, height: 36, borderRadius: '50%', bgcolor: isDark ? 'rgba(34, 197, 94, 0.15)' : '#dcfce7', 
                  color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                }}>
                  <SettingsIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">Configuration</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.5 }}>Configure labels and other options.</Typography>
                </Box>
              </Box>

              {/* Step 4: Preview */}
              <Box sx={{ 
                px: 2, py: 1.5, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'flex-start'
              }}>
                <Box sx={{ 
                  width: 36, height: 36, borderRadius: '50%', bgcolor: isDark ? 'rgba(239, 68, 68, 0.15)' : '#fee2e2', 
                  color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 
                }}>
                  <VisibilityIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary">Preview</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mt: 0.5 }}>Review and save your chart.</Typography>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Right Content */}
          <Box sx={{ height: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', width: { xs: '100%', md: 'calc(100% - 300px)' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ 
              px: 4, 
              pb: 4, 
              flexGrow: 1, 
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '4px' },
              '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
            }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Title <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    name="chart_custom_title_input"
                    placeholder="Enter chart title"
                    autoComplete="off"
                    fullWidth 
                    size="small"
                    value={form.title} 
                    onChange={(e) => setForm({ ...form, title: e.target.value })} 
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><DescriptionIcon fontSize="small" /></Box>,
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Description <span style={{color: 'text.secondary', fontWeight: 'normal'}}>(Optional)</span></Typography>
                  <TextField 
                    name="chart_custom_desc_input"
                    placeholder="Enter chart description"
                    autoComplete="off"
                    fullWidth 
                    size="small"
                    value={form.description} 
                    onChange={(e) => setForm({ ...form, description: e.target.value })} 
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><DescriptionIcon fontSize="small" /></Box>,
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Chart Type <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    select
                    fullWidth
                    size="small"
                    value={form.chartType} 
                    name="chart_custom_type_select"
                    onChange={(e) => setForm({ ...form, chartType: e.target.value })}
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><BarChartIcon fontSize="small" /></Box>,
                      sx: { borderRadius: 2 }
                    }}
                  >
                    {chartTypes.map((item) => (
                      <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Model <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    select
                    fullWidth
                    size="small"
                    value={form.modelName} 
                    name="chart_custom_model_select"
                    onChange={(e) => setForm({ ...form, modelName: e.target.value, xField: "createdAt", yField: "_id" })}
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><CategoryIcon fontSize="small" /></Box>,
                      sx: { borderRadius: 2 }
                    }}
                  >
                    <MenuItem value="" disabled>Select model</MenuItem>
                    {models.map((model) => (
                      <MenuItem key={model.modelName} value={model.modelName}>{model.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Aggregation <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    select
                    fullWidth
                    size="small"
                    value={form.aggregationType} 
                    name="chart_custom_agg_select"
                    onChange={(e) => setForm({ ...form, aggregationType: e.target.value })}
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', ml: 0.5, mr: 1, display: 'flex', alignItems: 'center', fontFamily: 'serif', fontSize: '1.2rem' }}>&Sigma;</Box>,
                      sx: { borderRadius: 2 }
                    }}
                  >
                    {aggregationTypes.map((item) => (
                      <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>X Axis Field <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    select
                    fullWidth
                    size="small"
                    value={form.xField} 
                    name="chart_custom_xaxis_select"
                    onChange={(e) => setForm({ ...form, xField: e.target.value })}
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><ViewListIcon fontSize="small" /></Box>,
                      sx: { borderRadius: 2 }
                    }}
                  >
                    <MenuItem value="" disabled>Select X axis field</MenuItem>
                    {fields.map((field) => (
                      <MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Y Axis Field <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    select
                    fullWidth
                    size="small"
                    value={form.yField} 
                    name="chart_custom_yaxis_select"
                    onChange={(e) => setForm({ ...form, yField: e.target.value })}
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1, display: 'flex' }}><ViewListIcon fontSize="small" /></Box>,
                      sx: { borderRadius: 2 }
                    }}
                  >
                    <MenuItem value="" disabled>Select Y axis field</MenuItem>
                    {fields.map((field) => (
                      <MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>X Label <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    name="chart_custom_xlabel_input"
                    placeholder="Enter X axis label"
                    autoComplete="off"
                    fullWidth 
                    size="small"
                    value={form.xLabel} 
                    onChange={(e) => setForm({ ...form, xLabel: e.target.value })} 
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1.5, ml: 0.5, display: 'flex', fontWeight: 'bold', fontSize: '1.1rem', fontFamily: 'serif' }}>T</Box>,
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block', color: 'text.primary' }}>Y Label <span style={{color: 'red'}}>*</span></Typography>
                  <TextField 
                    name="chart_custom_ylabel_input"
                    placeholder="Enter Y axis label"
                    autoComplete="off"
                    fullWidth 
                    size="small"
                    value={form.yLabel} 
                    onChange={(e) => setForm({ ...form, yLabel: e.target.value })} 
                    InputProps={{
                      startAdornment: <Box sx={{ color: 'text.secondary', mr: 1.5, ml: 0.5, display: 'flex', fontWeight: 'bold', fontSize: '1.1rem', fontFamily: 'serif' }}>T</Box>,
                      sx: { borderRadius: 2 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1} color="text.primary">Preview</Typography>
                    
                    {!previewData.length ? (
                      <Box 
                        sx={{ 
                          height: 240, 
                          bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                          borderRadius: 3, 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          justifyContent: 'center',
                          gap: 2,
                          border: isDark ? '1px dashed rgba(255,255,255,0.1)' : 'none'
                        }}
                      >
                        <Box sx={{ 
                          width: 80, height: 80, 
                          borderRadius: '50%', 
                          border: isDark ? '2px dashed rgba(59, 130, 246, 0.4)' : '2px dashed #bfdbfe',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          position: 'relative'
                        }}>
                          <BarChartIcon sx={{ color: '#3b82f6', fontSize: 40 }} />
                          <Box sx={{ position: 'absolute', width: 4, height: 4, bgcolor: '#93c5fd', borderRadius: '50%', top: 10, right: 10 }} />
                          <Box sx={{ position: 'absolute', width: 6, height: 6, bgcolor: '#bfdbfe', borderRadius: '50%', bottom: 20, left: -5 }} />
                          <Box sx={{ position: 'absolute', width: 4, height: 4, bgcolor: '#60a5fa', borderRadius: '50%', top: -5, left: 30 }} />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          Preview will appear here after you run the preview.
                        </Typography>
                        
                        <Button 
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={previewChart}
                          disabled={loading || !form.modelName}
                          sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            borderColor: '#bfdbfe',
                            color: '#3b82f6'
                          }}
                        >
                          Preview
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ p: 2, border: isDark ? '1px dashed rgba(255,255,255,0.2)' : '1px dashed #bfdbfe', borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', position: 'relative' }}>
                        <Button 
                          variant="outlined"
                          size="small"
                          startIcon={<PlayArrowIcon />}
                          onClick={previewChart}
                          disabled={loading || !form.modelName}
                          sx={{ 
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            borderRadius: 2, 
                            textTransform: 'none', 
                            fontWeight: 'bold',
                            borderColor: '#bfdbfe',
                            bgcolor: isDark ? 'var(--canvas)' : 'white',
                            zIndex: 10
                          }}
                        >
                          Refresh
                        </Button>
                        <Box sx={{ height: 240, mt: 3 }}>
                          {renderChart}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Footer */}
            <Box sx={{ p: { xs: 2, md: 3 }, px: { xs: 2, md: 4 }, borderTop: '1px solid var(--hairline)', display: 'flex', justifyContent: 'flex-end', gap: { xs: 1, md: 2 }, bgcolor: isDark ? 'var(--canvas)' : 'white' }}>
              <Button 
                onClick={handleClose} 
                variant="outlined"
                sx={{
                  color: 'var(--vault-navy)',
                  borderColor: 'var(--hairline)',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  textTransform: 'none',
                  borderRadius: '6px',
                  height: '40px',
                  px: { xs: 2, md: 4 },
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: 'var(--mist)',
                    borderColor: 'var(--slate)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={saveChart}
                variant="contained"
                sx={{
                  bgcolor: 'var(--edge-blue)',
                  color: '#FFF',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '6px',
                  height: '40px',
                  px: { xs: 2, md: 4 },
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  border: '1px solid var(--edge-blue)',
                  '&:hover': {
                    bgcolor: 'var(--edge-blue)',
                    boxShadow: '0 0 0 3px var(--blue-tint)',
                  },
                }}
              >
                {editingId ? 'Update Chart' : 'Create Chart'}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Dialog>
      
      <Snackbar open={snackOpen} autoHideDuration={3000} onClose={handleSnackClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CustomChartsPage;